import { chatsApi } from 'app/services/api';
import {
  getChannelId,
  getChannelMembers,
  getMemberId,
  getMessageTimestamp,
  resolveKnownMemberDisplayNames,
} from 'app/shared/components/TeamChat/TeamChat.utils';
import { isNil } from 'lodash';

import {
  TEAM_CHAT_MESSAGES_PAGE_SIZE,
  TeamChatActionType,
  TeamChatMemberIdType,
  TeamChatMessagePayloadType,
} from 'constants/teamChat';
import { catchAsyncActionError } from 'utils/action';
import { getConditionalObject } from 'utils/data';
import { logger } from 'utils/logger';
import { getUUID } from 'utils/uuid';

import { getUsersAction } from 'store/users/actions';

export const fetchChannelsAction = () => async dispatch => {
  dispatch({ type: TeamChatActionType.FETCH_CHANNELS });

  try {
    const { data } = await chatsApi.getUserChatsWithLoanHeaders();

    dispatch({
      type: TeamChatActionType.FETCH_CHANNELS_SUCCESS,
      payload: { channels: data ?? [] },
    });

    return data;
  } catch (error) {
    dispatch({
      type: TeamChatActionType.FETCH_CHANNELS_FAILURE,
      payload: error?.message ?? null,
    });

    return catchAsyncActionError(error);
  }
};

export const initializeChannelAction =
  ({
    expectedChatId,
    chatName,
    currentUserId,
    companyId,
    subCompanyId = null,
    isSubCompanyUser = false,
    knownMemberNamesByUserId = {},
  }) =>
  async dispatch => {
    const { data } = await chatsApi.getChatInfo({ chatId: expectedChatId });

    const initialChat = data?.length
      ? data[0]
      : (
          await chatsApi.createChat({
            id: expectedChatId,
            name: chatName,
            users: [],
            // TPO channel only: scope the new chat to the loan's sub-company (the broker's) so the
            // backend does not need a cross-service Loans lookup (which 500s — Loans is not in
            // Notifications' dev ServiceDiscovery). Internal/global channels pass null and omit it.
            ...getConditionalObject(!isNil(subCompanyId), { subCompanyId }),
          })
        ).data;

    const connectionChatId = getChannelId(initialChat);
    const initialMembers = getChannelMembers(initialChat);
    const hasCurrentUser = initialMembers.some(member => getMemberId(member) === currentUserId);

    const chat =
      !hasCurrentUser && connectionChatId
        ? await chatsApi
            .addCurrentUserToChat({ chatId: connectionChatId })
            .then(() => chatsApi.getChatInfo({ chatId: connectionChatId }))
            .then(response => response.data?.[0] ?? initialChat)
        : initialChat;

    const members = getChannelMembers(chat);
    const chatUsersIds = members.map(getMemberId).filter(Boolean);

    if (chatUsersIds.length && !isSubCompanyUser) {
      // Member-name enrichment via `/api/users` is best-effort, not load-bearing: the channel/member
      // payload already carries display names (see TeamChat.utils#getMemberNickname), which is what the
      // chat UI actually renders from. Sub-company (broker) users are structurally forbidden from
      // `/api/users` (403) even though they can read the chat itself, and their member names now arrive
      // resolved on the chat payload (server-side, under a system context — DEV-4226), so skip the call
      // entirely for them rather than firing a request that always 403s — NO `/api/users` call is
      // required to render broker chat names. For employees keep it non-fatal so a transient/unexpected
      // failure degrades gracefully instead of erroring out the whole drawer.
      await dispatch(getUsersAction({ companyId, id: chatUsersIds })).catch(error => {
        logger.error(
          '[initializeChannelAction] Failed to enrich chat members via /api/users',
          error,
        );
      });
    }

    if (connectionChatId) {
      // A sub-company (broker) member's name arrives from the Chats API as their raw id (the
      // server-side resolver that gives employees a real name needs Accounts /api/users, which a
      // broker 403s on — see the enrichment comment above). Fall back to names already loaded
      // client-side for this loan (role assignees + the viewer's own name) instead of a GUID
      // (DEV-4226). No-op for employees, whose members already carry a resolved name.
      const displayMembers = isSubCompanyUser
        ? resolveKnownMemberDisplayNames(members, knownMemberNamesByUserId)
        : members;

      dispatch({
        type: TeamChatActionType.FETCH_CHANNEL_SUCCESS,
        payload: {
          chatId: connectionChatId,
          channel: {
            id: connectionChatId,
            name: chatName,
            members: displayMembers,
          },
        },
      });
    }

    return connectionChatId;
  };

export const escalateTpoChatAction =
  ({ chatId }) =>
  async () => {
    try {
      const { data } = await chatsApi.escalateChat({ chatId });

      return data;
    } catch (error) {
      return catchAsyncActionError(error);
    }
  };

export const fetchMessagesAction =
  ({ chatId, before = null, limit = TEAM_CHAT_MESSAGES_PAGE_SIZE }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.FETCH_MESSAGES, payload: { chatId } });

    try {
      const { data } = await chatsApi.getMessages({ chatId, before, limit });

      const messages = data ?? [];
      const hasMore = messages.length >= limit;
      const oldestMessage = messages[0] ?? null;
      const nextBefore = getMessageTimestamp(oldestMessage);

      dispatch({
        type: TeamChatActionType.FETCH_MESSAGES_SUCCESS,
        payload: { chatId, messages, cursor: nextBefore, hasMore, isInitial: !before },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.FETCH_MESSAGES_FAILURE,
        payload: { chatId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const sendMessageAction =
  ({
    chatId,
    currentUserId,
    message,
    replyToMessageId = null,
    subjectRef = null,
    sendSmsForOfflineUsers = false,
  }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.SEND_MESSAGE, payload: { chatId } });

    try {
      const senderId = { type: TeamChatMemberIdType.VIA_USER_ID, userId: currentUserId };
      const payload = { type: TeamChatMessagePayloadType.TEXT, message };

      const { data } = await chatsApi.sendMessage({
        chatId,
        senderId,
        payload,
        replyToMessageId,
        subjectRef,
        sendSmsForOfflineUsers,
      });

      dispatch({
        type: TeamChatActionType.SEND_MESSAGE_SUCCESS,
        payload: { chatId, message: data },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.SEND_MESSAGE_FAILURE,
        payload: { chatId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const updateMessageAction =
  ({ chatId, messageId, currentUserId, message }) =>
  async dispatch => {
    dispatch({
      type: TeamChatActionType.UPDATE_MESSAGE,
      payload: { chatId, messageId },
    });

    try {
      const senderId = { type: TeamChatMemberIdType.VIA_USER_ID, userId: currentUserId };
      const payload = { type: TeamChatMessagePayloadType.TEXT, message };

      const { data } = await chatsApi.updateMessage({ chatId, messageId, senderId, payload });

      dispatch({
        type: TeamChatActionType.UPDATE_MESSAGE_SUCCESS,
        payload: { chatId, messageId, message: data },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.UPDATE_MESSAGE_FAILURE,
        payload: { chatId, messageId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const markAsReadAction =
  ({ chatId, readAt }) =>
  async dispatch => {
    const readAtIso = readAt ?? new Date().toISOString();

    dispatch({
      type: TeamChatActionType.MARK_AS_READ,
      payload: { chatId, readAt: readAtIso },
    });

    try {
      await chatsApi.markAsRead({ chatId, readAt: readAtIso });
    } catch (error) {
      return catchAsyncActionError(error);
    }
  };

export const setActiveChannelAction = chatId => ({
  type: TeamChatActionType.SET_ACTIVE_CHANNEL,
  payload: chatId,
});

export const clearActiveChannelIfMatchesAction = chatId => (dispatch, getState) => {
  const currentActiveChannelId = getState().teamChat.activeChannelId ?? null;

  if (currentActiveChannelId !== chatId) {
    return;
  }

  dispatch({ type: TeamChatActionType.SET_ACTIVE_CHANNEL, payload: null });
};

export const messageReceivedAction = ({ chatId, message }) => ({
  type: TeamChatActionType.MESSAGE_RECEIVED,
  payload: { chatId, message },
});

export const messagesReadAction = ({ chatId, chatUserId, lastReadAt }) => ({
  type: TeamChatActionType.MESSAGES_READ,
  payload: { chatId, chatUserId, lastReadAt },
});

export const typingStartedAction = ({ chatId, chatUserId, memberId = null }) => ({
  type: TeamChatActionType.TYPING_STARTED,
  payload: { chatId, chatUserId, memberId },
});

export const typingStoppedAction = ({ chatId, chatUserId }) => ({
  type: TeamChatActionType.TYPING_STOPPED,
  payload: { chatId, chatUserId },
});

export const messageUpdatedAction = ({ chatId, messageId, payload, updated }) => ({
  type: TeamChatActionType.MESSAGE_UPDATED,
  payload: { chatId, messageId, messagePayload: payload, updated },
});

export const addReactionAction =
  ({ chatId, messageId, emoji }) =>
  async dispatch => {
    dispatch({
      type: TeamChatActionType.ADD_REACTION,
      payload: { chatId, messageId, emoji },
    });

    try {
      const { data } = await chatsApi.addReaction({ chatId, messageId, emoji });

      dispatch({
        type: TeamChatActionType.ADD_REACTION_SUCCESS,
        payload: { chatId, messageId, reaction: data },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.ADD_REACTION_FAILURE,
        payload: { chatId, messageId, emoji, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const removeReactionAction =
  ({ chatId, messageId, emoji }) =>
  async dispatch => {
    dispatch({
      type: TeamChatActionType.REMOVE_REACTION,
      payload: { chatId, messageId, emoji },
    });

    try {
      await chatsApi.removeReaction({ chatId, messageId, emoji });

      dispatch({
        type: TeamChatActionType.REMOVE_REACTION_SUCCESS,
        payload: { chatId, messageId, emoji },
      });
    } catch (error) {
      dispatch({
        type: TeamChatActionType.REMOVE_REACTION_FAILURE,
        payload: { chatId, messageId, emoji, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const reactionAddedAction = ({ chatId, messageId, reaction }) => ({
  type: TeamChatActionType.REACTION_ADDED,
  payload: { chatId, messageId, reaction },
});

export const reactionRemovedAction = ({ chatId, messageId, chatUserId, emoji }) => ({
  type: TeamChatActionType.REACTION_REMOVED,
  payload: { chatId, messageId, chatUserId, emoji },
});

export const uploadAttachmentAction =
  ({ chatId, file, sendSmsForOfflineUsers = false }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.UPLOAD_ATTACHMENT, payload: { chatId } });

    try {
      const { data } = await chatsApi.uploadAttachment({ chatId, file, sendSmsForOfflineUsers });

      dispatch({
        type: TeamChatActionType.UPLOAD_ATTACHMENT_SUCCESS,
        payload: { chatId, message: data },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.UPLOAD_ATTACHMENT_FAILURE,
        payload: { chatId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const fetchPresenceAction =
  ({ chatId }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.FETCH_PRESENCE, payload: { chatId } });

    try {
      const { data } = await chatsApi.getPresence({ chatId });

      dispatch({
        type: TeamChatActionType.FETCH_PRESENCE_SUCCESS,
        payload: { chatId, chatUserIds: data ?? [] },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.FETCH_PRESENCE_FAILURE,
        payload: { chatId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const userOnlineAction = ({ chatId, chatUserId, memberId = null }) => ({
  type: TeamChatActionType.USER_ONLINE,
  payload: { chatId, chatUserId, memberId },
});

export const userOfflineAction = ({ chatId, chatUserId, memberId = null }) => ({
  type: TeamChatActionType.USER_OFFLINE,
  payload: { chatId, chatUserId, memberId },
});

export const pinMessageAction =
  ({ chatId, messageId }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.PIN_MESSAGE, payload: { chatId, messageId } });

    try {
      const { data } = await chatsApi.pinMessage({ chatId, messageId });

      dispatch({
        type: TeamChatActionType.PIN_MESSAGE_SUCCESS,
        payload: { chatId, messageId, message: data },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.PIN_MESSAGE_FAILURE,
        payload: { chatId, messageId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const unpinMessageAction =
  ({ chatId, messageId }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.UNPIN_MESSAGE, payload: { chatId, messageId } });

    try {
      await chatsApi.unpinMessage({ chatId, messageId });

      dispatch({
        type: TeamChatActionType.UNPIN_MESSAGE_SUCCESS,
        payload: { chatId, messageId },
      });
    } catch (error) {
      dispatch({
        type: TeamChatActionType.UNPIN_MESSAGE_FAILURE,
        payload: { chatId, messageId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const fetchPinnedMessagesAction =
  ({ chatId }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.FETCH_PINNED_MESSAGES, payload: { chatId } });

    try {
      const { data } = await chatsApi.getPinnedMessages({ chatId });

      dispatch({
        type: TeamChatActionType.FETCH_PINNED_MESSAGES_SUCCESS,
        payload: { chatId, messages: data ?? [] },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.FETCH_PINNED_MESSAGES_FAILURE,
        payload: { chatId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const messagePinnedAction = ({ chatId, messageId, pinnedAt = null }) => ({
  type: TeamChatActionType.MESSAGE_PINNED,
  payload: { chatId, messageId, pinnedAt },
});

export const messageUnpinnedAction = ({ chatId, messageId }) => ({
  type: TeamChatActionType.MESSAGE_UNPINNED,
  payload: { chatId, messageId },
});

export const pinChatAction =
  ({ chatId }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.PIN_CHAT, payload: { chatId } });

    try {
      await chatsApi.pinChat({ chatId });

      dispatch({
        type: TeamChatActionType.PIN_CHAT_SUCCESS,
        payload: { chatId, pinnedAt: new Date().toISOString() },
      });
    } catch (error) {
      dispatch({
        type: TeamChatActionType.PIN_CHAT_FAILURE,
        payload: { chatId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const unpinChatAction =
  ({ chatId }) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.UNPIN_CHAT, payload: { chatId } });

    try {
      await chatsApi.unpinChat({ chatId });

      dispatch({
        type: TeamChatActionType.UNPIN_CHAT_SUCCESS,
        payload: { chatId },
      });
    } catch (error) {
      dispatch({
        type: TeamChatActionType.UNPIN_CHAT_FAILURE,
        payload: { chatId, error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const fetchUserMentionsAction =
  ({ before = null, limit } = {}) =>
  async dispatch => {
    dispatch({ type: TeamChatActionType.FETCH_USER_MENTIONS });

    try {
      const { data } = await chatsApi.getUserMentions({ before, limit });

      dispatch({
        type: TeamChatActionType.FETCH_USER_MENTIONS_SUCCESS,
        payload: { mentions: data ?? [] },
      });

      return data;
    } catch (error) {
      dispatch({
        type: TeamChatActionType.FETCH_USER_MENTIONS_FAILURE,
        payload: { error: error?.message ?? null },
      });

      return catchAsyncActionError(error);
    }
  };

export const setConnectionStatusAction = status => ({
  type: TeamChatActionType.SET_CONNECTION_STATUS,
  payload: status,
});

// "Discuss in chat" (DEV-5448/DEV-5591) — queues an intent the loan chat drawer AND its composer
// react to from OUTSIDE their own subtree (a Financial Info / Borrowers row action). The caller
// picks the target `chatId` (internal or broker/TPO); the drawer opens itself and switches to that
// channel; the composer (the sole owner of its draft state) seeds the token once it sees a pending
// attachment addressed to its own chatId. A fresh `requestId` on every dispatch lets a second click
// of the SAME entity re-trigger the seed even though chatId/token match.
export const requestDiscussEntityInChatAction =
  ({ chatId, entityType, entityId, label }) =>
  dispatch => {
    if (!chatId || !entityType || !entityId) {
      return;
    }

    dispatch({
      type: TeamChatActionType.REQUEST_ENTITY_CHAT_ATTACHMENT,
      payload: { chatId, entityType, entityId, label, requestId: getUUID() },
    });
  };

export const clearEntityChatAttachmentAction = () => ({
  type: TeamChatActionType.CLEAR_ENTITY_CHAT_ATTACHMENT,
});

// Reference-badge click (DEV-5446) — the counterpart queued intent to `requestDiscussEntityInChatAction`
// above, reusing the SAME cross-subtree mechanism: a badge (Financial Info / Borrowers row) sits
// outside the drawer's subtree, so it queues an intent rather than reaching into drawer/rail state
// directly. `LoanEditorChatDrawerInner` opens + switches to Internal on it; `TeamChatChannel` applies
// it to its own `activeEntityFilter` rail state and clears it. The badge already carries its own
// `chatId` (unlike "Discuss in chat", which only has `applicationId`), so this takes `chatId` directly
// rather than deriving it. This is a client-side stand-in — the server-side `?subjectRefPath=` filter
// (DEV-5447) is the eventual upgrade path, not built here.
export const requestEntityChatFilterAction =
  ({ chatId, entityType, entityId, label }) =>
  dispatch => {
    if (!chatId || !entityType || !entityId) {
      return;
    }

    dispatch({
      type: TeamChatActionType.REQUEST_ENTITY_CHAT_FILTER,
      payload: { chatId, entityType, entityId, label, requestId: getUUID() },
    });
  };

export const clearEntityChatFilterAction = () => ({
  type: TeamChatActionType.CLEAR_ENTITY_CHAT_FILTER,
});
